import * as THREE from 'three';
import { BaseRenderer } from './BaseRenderer.js';
import { isDebugMode } from '../utils/debug.js';
import { UI_CONFIG, PROJECTILE_CONFIG, THREE_RENDERER_CONFIG, DEBUG_VISUALIZATION_CONFIG } from '../config.js';
import { Corpse } from '../entities/Corpse.js';
import { Metal } from '../entities/Metal.js';
import { Nebula } from '../entities/Nebula.js';
import { Crystal } from '../entities/Crystal.js';

/**
 * ThreeJSRenderer - 3D WebGL rendering with Three.js
 *
 * ⚠️ CRITICAL: DUAL RENDERER SYSTEM
 * This is one of TWO renderers that users can toggle between (R key).
 * Any visual feature added here MUST also exist in Canvas2DRenderer.
 *
 * See: docs/RENDERER_PARITY.md for detailed parity requirements
 *
 * Rendering Approach:
 * - WebGL via Three.js library
 * - 3D scene with orthographic camera (top-down view)
 * - Dynamic lighting and shadows
 * - Particle effects
 * - Text requires canvas texture sprites
 * - Coordinate system: (0,0) = center of screen
 *
 * Visual Enhancements (ThreeJS-only, OK to not be in Canvas2D):
 * - Dynamic point lights (missiles, cursor)
 * - Shadow mapping
 * - Particle effects (explosions, exhaust, nebula particles)
 * - Tractor beams from bases
 * - Emissive materials
 * - Advanced lighting (ambient, directional, spotlight)
 *
 * Debug Features (MUST match Canvas2D):
 * - ✅ Target lines (red lines from soldier to target)
 * - ✅ Predicted aim points (white X markers)
 * - ✅ Off-screen prediction count (purple "OS:N" text)
 * - ✅ Target IDs (yellow "#5 -> #12" text)
 * - ✅ Respects DEBUG_VISUALIZATION_CONFIG flags
 *
 * ✅ PARITY STATUS: COMPLETE
 * All debug features now match Canvas2D renderer.
 * Users see consistent debug visualization in both renderers.
 *
 * Implementation Details:
 * - Text uses THREE.Sprite with canvas texture
 * - Coordinates convert from top-left to center: x - width/2, -(y - height/2)
 * - Y axis is flipped in Three.js (positive Y = up, negative Y = down)
 * - Debug markers use z=15-20 to appear above units
 * - debugMarkers and debugSprites arrays cleaned up each frame
 *
 * Performance:
 * - Object pooling for common meshes (soldiers, projectiles, etc.)
 * - Reuse materials to reduce draw calls
 * - Clean up debug objects each frame to prevent memory leaks
 */
export class ThreeJSRenderer extends BaseRenderer {
  constructor(canvas) {
    super(canvas);
    
    // Three.js setup
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(THREE_RENDERER_CONFIG.SCENE.BACKGROUND_COLOR);
    
    // Orthographic camera for 2D-like view with slight angle
    const aspect = canvas.width / canvas.height;
    const frustumSize = canvas.height;
    this.camera = new THREE.OrthographicCamera(
      -frustumSize * aspect / 2,
      frustumSize * aspect / 2,
      frustumSize / 2,
      -frustumSize / 2,
      0.1,
      1000
    );
    // Position camera straight down for top-down view
    this.camera.position.set(0, 0, 500);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
    
    // Renderer
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setSize(canvas.width, canvas.height);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    
    // Configure renderer for better lighting
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = THREE_RENDERER_CONFIG.RENDERER.TONE_MAPPING_EXPOSURE;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    
    // Lighting
    const ambientLight = new THREE.AmbientLight(THREE_RENDERER_CONFIG.LIGHTING.AMBIENT.COLOR, THREE_RENDERER_CONFIG.LIGHTING.AMBIENT.INTENSITY); // Lower ambient for more contrast
    this.scene.add(ambientLight);
    
    // Main directional light (fixed position)
    this.directionalLight = new THREE.DirectionalLight(THREE_RENDERER_CONFIG.LIGHTING.DIRECTIONAL.COLOR, THREE_RENDERER_CONFIG.LIGHTING.DIRECTIONAL.INTENSITY); // Brighter main light
    this.directionalLight.position.set(200, -200, 300);
    this.directionalLight.castShadow = true;
    
    // Add a spotlight that follows cursor
    this.spotlight = new THREE.SpotLight(THREE_RENDERER_CONFIG.SPOTLIGHT_COLOR, THREE_RENDERER_CONFIG.SPOTLIGHT_INTENSITY, THREE_RENDERER_CONFIG.SPOTLIGHT_DISTANCE, THREE_RENDERER_CONFIG.SPOTLIGHT_ANGLE, THREE_RENDERER_CONFIG.SPOTLIGHT_PENUMBRA, THREE_RENDERER_CONFIG.SPOTLIGHT_DECAY);
    this.spotlight.position.set(0, 0, 200);
    this.spotlight.castShadow = false;
    
    // Create target for spotlight
    this.spotlightTarget = new THREE.Object3D();
    this.scene.add(this.spotlightTarget);
    this.spotlight.target = this.spotlightTarget;
    this.scene.add(this.spotlight);
    
    // Configure shadow properties for better quality
    this.directionalLight.shadow.mapSize.width = 2048;
    this.directionalLight.shadow.mapSize.height = 2048;
    this.directionalLight.shadow.camera.near = 0.5;
    this.directionalLight.shadow.camera.far = 1000;
    
    // Set shadow camera bounds to cover game area
    const shadowSize = 600;
    this.directionalLight.shadow.camera.left = -shadowSize;
    this.directionalLight.shadow.camera.right = shadowSize;
    this.directionalLight.shadow.camera.top = shadowSize;
    this.directionalLight.shadow.camera.bottom = -shadowSize;
    
    this.scene.add(this.directionalLight);
    
    // Removed fill light to make point lights more visible
    
    // Add ground plane to receive shadows and light
    const groundGeometry = new THREE.PlaneGeometry(2000, 2000);
    const groundMaterial = new THREE.MeshStandardMaterial({ 
      color: THREE_RENDERER_CONFIG.GROUND.COLOR, // Even lighter for better visibility
      roughness: 0.5, // Medium roughness
      metalness: 0.0 // No metalness for diffuse reflection
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.receiveShadow = true;
    ground.position.z = -10;
    this.scene.add(ground);
    
    // Object pools for performance
    this.soldierMeshPool = [];
    this.projectileMeshPool = [];
    this.missileMeshPool = [];
    this.baseMeshPool = [];
    this.metalMeshPool = [];
    this.nebulaMeshPool = [];
    this.crystalMeshPool = [];
    this.corpseMeshPool = [];
    
    // Particle effects
    this.exhaustParticles = [];
    this.explosionParticles = [];
    
    // Nebula particles
    this.nebulaParticles = new Map(); // Map nebula resource to its particle system
    this.nebulaTextSprites = new Map(); // Map nebula resource to its text sprites
    
    // Missile lights
    this.missileLights = new Map(); // Map projectile to its light
    
    // Track units for death explosions
    this.previousUnits = new Set(); // Track units from previous frame

    // Debug animation state
    this.debugAnimationState = 'hidden'; // 'hidden', 'growing', 'visible', 'shrinking'
    this.debugAnimationProgress = 0; // 0 to 1
    this.debugAnimationSpeed = 0.05; // Speed per frame (0.05 = ~20 frames = ~333ms at 60fps)
    this.entityAnimationDelays = new WeakMap(); // Store random delays (0-1) per entity
    this.entityAnimationProgress = new WeakMap(); // Store animation progress (0-1) per entity

    // Cursor light for testing - load saved settings from localStorage
    const savedIntensity = parseFloat(localStorage.getItem('cursorLightIntensity') || '1000');
    const savedDistance = parseFloat(localStorage.getItem('cursorLightDistance') || '300');
    const savedColorString = localStorage.getItem('cursorLightColor') || '0xffffff';
    // Parse hex string to number (localStorage stores as string, Three.js needs number)
    const savedColor = parseInt(savedColorString, 16);

    this.cursorLight = new THREE.PointLight(savedColor, savedIntensity, savedDistance);
    this.cursorLight.castShadow = true;
    this.scene.add(this.cursorLight);
    
    // Track mouse position
    this.mouseX = 0;
    this.mouseY = 0;
    this.canvas.addEventListener('mousemove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      this.mouseX = e.clientX - rect.left;
      this.mouseY = e.clientY - rect.top;
    });
    
    // Keyboard controls for cursor light
    window.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp') {
        this.cursorLight.intensity = Math.min(THREE_RENDERER_CONFIG.LIGHTING.CURSOR.MAX_INTENSITY, this.cursorLight.intensity + THREE_RENDERER_CONFIG.LIGHTING.CURSOR.INTENSITY_INCREMENT);
        localStorage.setItem('cursorLightIntensity', this.cursorLight.intensity.toString());
        console.log('Cursor light intensity:', this.cursorLight.intensity);
      } else if (e.key === 'ArrowDown') {
        this.cursorLight.intensity = Math.max(0, this.cursorLight.intensity - THREE_RENDERER_CONFIG.LIGHTING.CURSOR.INTENSITY_INCREMENT);
        localStorage.setItem('cursorLightIntensity', this.cursorLight.intensity.toString());
        console.log('Cursor light intensity:', this.cursorLight.intensity);
      } else if (e.key === 'ArrowRight') {
        this.cursorLight.distance = Math.min(THREE_RENDERER_CONFIG.LIGHTING.CURSOR.MAX_DISTANCE, this.cursorLight.distance + THREE_RENDERER_CONFIG.LIGHTING.CURSOR.DISTANCE_INCREMENT);
        localStorage.setItem('cursorLightDistance', this.cursorLight.distance.toString());
        console.log('Cursor light distance:', this.cursorLight.distance);
      } else if (e.key === 'ArrowLeft') {
        this.cursorLight.distance = Math.max(THREE_RENDERER_CONFIG.LIGHTING.CURSOR.MIN_DISTANCE, this.cursorLight.distance - THREE_RENDERER_CONFIG.LIGHTING.CURSOR.DISTANCE_INCREMENT);
        localStorage.setItem('cursorLightDistance', this.cursorLight.distance.toString());
        console.log('Cursor light distance:', this.cursorLight.distance);
      } else if (e.key === '1') {
        this.cursorLight.color.setHex(THREE_RENDERER_CONFIG.LIGHTING.CURSOR.COLOR_WHITE); // White
        localStorage.setItem('cursorLightColor', String(THREE_RENDERER_CONFIG.LIGHTING.CURSOR.COLOR_WHITE));
        console.log('Cursor light color: white');
      } else if (e.key === '2') {
        this.cursorLight.color.setHex(THREE_RENDERER_CONFIG.LIGHTING.CURSOR.COLOR_ORANGE); // Orange
        localStorage.setItem('cursorLightColor', String(THREE_RENDERER_CONFIG.LIGHTING.CURSOR.COLOR_ORANGE));
        console.log('Cursor light color: orange');
      } else if (e.key === '3') {
        this.cursorLight.color.setHex(THREE_RENDERER_CONFIG.LIGHTING.CURSOR.COLOR_YELLOW); // Yellow
        localStorage.setItem('cursorLightColor', String(THREE_RENDERER_CONFIG.LIGHTING.CURSOR.COLOR_YELLOW));
        console.log('Cursor light color: yellow');
      }
    });
    
    // Materials cache
    this.teamMaterials = {};
    
    // Create radial gradient texture for nebula
    const nebulaCanvas = document.createElement('canvas');
    nebulaCanvas.width = 256;
    nebulaCanvas.height = 256;
    const nebulaCtx = nebulaCanvas.getContext('2d');
    
    // Create radial gradient
    const gradient = nebulaCtx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, THREE_RENDERER_CONFIG.MISC.GRADIENT_COLORS.NEBULA_CENTER);  // Center - more opaque
    gradient.addColorStop(0.5, THREE_RENDERER_CONFIG.MISC.GRADIENT_COLORS.NEBULA_MID); // Mid - purple tint
    gradient.addColorStop(1, THREE_RENDERER_CONFIG.MISC.GRADIENT_COLORS.NEBULA_EDGE);      // Edge - fully transparent
    
    nebulaCtx.fillStyle = gradient;
    nebulaCtx.fillRect(0, 0, 256, 256);
    
    const nebulaTexture = new THREE.CanvasTexture(nebulaCanvas);
    
    this.resourceMaterials = {
      metal: new THREE.MeshStandardMaterial({ 
        color: THREE_RENDERER_CONFIG.MATERIALS.METAL.COLOR, 
        metalness: THREE_RENDERER_CONFIG.MATERIALS.METAL.METALNESS, 
        roughness: THREE_RENDERER_CONFIG.MATERIALS.METAL.ROUGHNESS 
      }),
      nebula: new THREE.MeshStandardMaterial({ 
        map: nebulaTexture,
        transparent: true, 
        opacity: THREE_RENDERER_CONFIG.MATERIALS.NEBULA.OPACITY,  // Let the texture handle transparency
        emissive: THREE_RENDERER_CONFIG.MATERIALS.NEBULA.EMISSIVE,  // Pink glow
        emissiveIntensity: THREE_RENDERER_CONFIG.MATERIALS.NEBULA.EMISSIVE_INTENSITY,
        roughness: THREE_RENDERER_CONFIG.MATERIALS.NEBULA.ROUGHNESS,
        metalness: THREE_RENDERER_CONFIG.MATERIALS.NEBULA.METALNESS,
        side: THREE.DoubleSide,
        depthWrite: false  // Prevent z-fighting with transparent materials
      }),
      crystal: new THREE.MeshStandardMaterial({ 
        color: THREE_RENDERER_CONFIG.MATERIALS.CRYSTAL.COLOR,
        emissive: THREE_RENDERER_CONFIG.GROUND.COLOR,  // No base emissive
        transparent: true,
        opacity: THREE_RENDERER_CONFIG.MATERIALS.CRYSTAL.OPACITY,
        roughness: THREE_RENDERER_CONFIG.MATERIALS.CRYSTAL.ROUGHNESS,
        metalness: THREE_RENDERER_CONFIG.MATERIALS.CRYSTAL.METALNESS
      })
    };
    
    // Geometries with more depth
    this.soldierGeometry = new THREE.BoxGeometry(THREE_RENDERER_CONFIG.GEOMETRY.SOLDIER.width, THREE_RENDERER_CONFIG.GEOMETRY.SOLDIER.length, THREE_RENDERER_CONFIG.GEOMETRY.SOLDIER.height); // Width, Length, Height - longer in Y for direction
    this.projectileGeometry = new THREE.CapsuleGeometry(THREE_RENDERER_CONFIG.GEOMETRY.PROJECTILE.radius, THREE_RENDERER_CONFIG.GEOMETRY.PROJECTILE.length, THREE_RENDERER_CONFIG.GEOMETRY.PROJECTILE.capSegments, THREE_RENDERER_CONFIG.GEOMETRY.PROJECTILE.radialSegments);
    this.missileGeometry = new THREE.ConeGeometry(THREE_RENDERER_CONFIG.GEOMETRY.MISSILE.radius, THREE_RENDERER_CONFIG.GEOMETRY.MISSILE.height, THREE_RENDERER_CONFIG.GEOMETRY.MISSILE.radialSegments); // Larger cone for missiles
    // Create base geometries - using cylinders for the pipe walls
    const pipeHeight = THREE_RENDERER_CONFIG.GEOMETRY.BASE.PIPE_HEIGHT;
    const outerRadius = THREE_RENDERER_CONFIG.GEOMETRY.BASE.OUTER_RADIUS;
    const innerRadius = THREE_RENDERER_CONFIG.GEOMETRY.BASE.INNER_RADIUS;
    
    // Outer cylinder
    this.baseOuterPipeGeometry = new THREE.CylinderGeometry(outerRadius, outerRadius, pipeHeight, THREE_RENDERER_CONFIG.GEOMETRY.BASE.SEGMENTS);
    
    // Inner cylinder (will be used to "cut out" the center)
    this.baseInnerPipeGeometry = new THREE.CylinderGeometry(innerRadius, innerRadius, pipeHeight, THREE_RENDERER_CONFIG.GEOMETRY.BASE.SEGMENTS);
    
    // Create ring geometry to connect the pipes at the top
    // RingGeometry: innerRadius, outerRadius, thetaSegments
    this.baseRingGeometry = new THREE.RingGeometry(innerRadius, outerRadius, THREE_RENDERER_CONFIG.GEOMETRY.BASE.SEGMENTS);
    
    // Floor disk geometry
    this.baseFloorGeometry = new THREE.CircleGeometry(outerRadius, THREE_RENDERER_CONFIG.GEOMETRY.BASE.SEGMENTS);
    this.metalGeometry = new THREE.BoxGeometry(THREE_RENDERER_CONFIG.GEOMETRY.METAL.SIZE, THREE_RENDERER_CONFIG.GEOMETRY.METAL.SIZE, THREE_RENDERER_CONFIG.GEOMETRY.METAL.SIZE);
    this.nebulaGeometry = new THREE.CircleGeometry(THREE_RENDERER_CONFIG.GEOMETRY.NEBULA.RADIUS, THREE_RENDERER_CONFIG.GEOMETRY.NEBULA.SEGMENTS); // Flat disk to avoid ground clipping
    this.crystalGeometry = new THREE.OctahedronGeometry(THREE_RENDERER_CONFIG.GEOMETRY.CRYSTAL.radius, THREE_RENDERER_CONFIG.GEOMETRY.CRYSTAL.detail);
    this.corpseGeometry = new THREE.BoxGeometry(THREE_RENDERER_CONFIG.GEOMETRY.CORPSE.width, THREE_RENDERER_CONFIG.GEOMETRY.CORPSE.height, THREE_RENDERER_CONFIG.GEOMETRY.CORPSE.depth);
    
    // Ensure all geometries have proper normals
    [this.soldierGeometry, this.projectileGeometry, this.missileGeometry, 
     this.baseOuterPipeGeometry, this.baseInnerPipeGeometry, this.baseRingGeometry,
     this.baseFloorGeometry, this.metalGeometry, 
     this.nebulaGeometry, this.crystalGeometry, this.corpseGeometry].forEach(geo => {
      geo.computeVertexNormals();
    });
    
    // Debug helpers
    this.debugLines = [];
    this.debugMarkers = [];  // For prediction point markers
    this.debugSprites = [];  // For text sprites (target IDs, OS counts)
    this.raycaster = new THREE.Raycaster();
  }
  
  getTeamMaterial(color) {
    if (!this.teamMaterials[color]) {
      const threeColor = new THREE.Color(color);
      this.teamMaterials[color] = new THREE.MeshStandardMaterial({
        color: threeColor,
        emissive: THREE_RENDERER_CONFIG.MATERIALS.TEAM.EMISSIVE_BLACK, // Black - no self-illumination
        emissiveIntensity: 0,
        roughness: THREE_RENDERER_CONFIG.MATERIALS.TEAM.ROUGHNESS,
        metalness: THREE_RENDERER_CONFIG.MATERIALS.TEAM.METALNESS
      });
    }
    return this.teamMaterials[color];
  }
  
  getMeshFromPool(pool, geometry, material) {
    let mesh = pool.find(m => !m.visible);
    if (!mesh) {
      // Create new mesh with its own material instance
      mesh = new THREE.Mesh(geometry, material.clone());
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      pool.push(mesh);
      this.scene.add(mesh);
    } else {
      // When reusing a mesh, always give it a fresh material clone
      // This prevents shared material state between entities
      mesh.material = material.clone();
    }
    mesh.visible = true;
    
    // Reset transformation to defaults
    mesh.position.set(0, 0, 0);
    mesh.rotation.set(0, 0, 0);
    mesh.scale.set(1, 1, 1);
    
    // Reset material properties to defaults
    mesh.material.emissive.setHex(THREE_RENDERER_CONFIG.MATERIALS.TEAM.EMISSIVE_BLACK);
    mesh.material.emissiveIntensity = 0;
    mesh.material.needsUpdate = true;
    
    // Don't clear userData - we want to preserve rotation offsets
    
    return mesh;
  }

  /**
   * Easing function for smooth animation
   * Uses easeOutCubic for deceleration at the end
   * @param {number} t - Progress from 0 to 1
   * @returns {number} - Eased value from 0 to 1
   */
  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * Get or initialize animation delay and progress for an entity
   * @param {Object} entity - The game entity (soldier or base)
   * @returns {Object} - { delay: 0-1, progress: 0-1 }
   */
  getEntityAnimation(entity) {
    if (!this.entityAnimationDelays.has(entity)) {
      // Random delay between 0 and 0.3 (30% of animation duration)
      this.entityAnimationDelays.set(entity, Math.random() * 0.3);
      this.entityAnimationProgress.set(entity, 0);
    }
    return {
      delay: this.entityAnimationDelays.get(entity),
      progress: this.entityAnimationProgress.get(entity)
    };
  }

  /**
   * Update animation progress for an entity
   * @param {Object} entity - The game entity
   * @param {number} globalProgress - Global animation progress (0-1)
   * @returns {number} - Entity-specific progress with delay and easing applied (0-1)
   */
  updateEntityAnimation(entity, globalProgress) {
    const { delay } = this.getEntityAnimation(entity);

    // Apply delay - entity starts animating when global progress > delay
    let localProgress = Math.max(0, (globalProgress - delay) / (1 - delay));
    localProgress = Math.min(1, localProgress);

    // Apply easing
    const easedProgress = this.easeOutCubic(localProgress);

    // Store for next frame
    this.entityAnimationProgress.set(entity, easedProgress);

    return easedProgress;
  }

  clearScene() {
    // Hide all pooled objects
    [...this.soldierMeshPool, ...this.projectileMeshPool, ...this.missileMeshPool,
     ...this.metalMeshPool, ...this.nebulaMeshPool,
     ...this.crystalMeshPool, ...this.corpseMeshPool].forEach(mesh => {
      mesh.visible = false;
    });

    // Hide base groups
    this.baseMeshPool.forEach(group => {
      group.visible = false;
    });

    // Clean up tractor beam lines
    if (this.tractorBeamLines) {
      this.tractorBeamLines.forEach(line => {
        this.scene.remove(line);
        line.geometry.dispose();
        line.material.dispose();
      });
      this.tractorBeamLines = [];
    }

    // Update and clean particles
    this.updateParticles();
    
    // Clean up any orphaned missile lights
    const lightsToRemove = [];
    for (const [projectile, light] of this.missileLights) {
      // Remove light if projectile is inactive, explosion finished, or if owner base is destroyed
      const ownerDestroyed = projectile.owner && projectile.owner.constructor.name === 'TeamBase' && !projectile.owner.alive;
      if (!projectile.active || 
          (projectile.exploding && projectile.explosionTimer > PROJECTILE_CONFIG.MISSILE_EXPLOSION_DURATION) ||
          ownerDestroyed) {
        lightsToRemove.push(projectile);
        this.scene.remove(light);
        light.dispose(); // Properly dispose of the light
      }
    }
    // Remove from map after iteration
    lightsToRemove.forEach(projectile => {
      this.missileLights.delete(projectile);
    });
  }

  /**
   * Clear all debug visualization objects (lines, markers, sprites)
   * Called at the start of renderDebugInfo() to clean up previous frame's debug visuals
   */
  clearDebugVisuals() {
    // Clear debug lines
    this.debugLines.forEach(line => {
      this.scene.remove(line);
      line.geometry.dispose();
      line.material.dispose();  // Fix memory leak - dispose materials
    });
    this.debugLines = [];

    // Clear debug markers (prediction points)
    this.debugMarkers.forEach(marker => {
      this.scene.remove(marker);
      marker.geometry.dispose();
      marker.material.dispose();
    });
    this.debugMarkers = [];

    // Clear debug sprites (text labels)
    this.debugSprites.forEach(sprite => {
      this.scene.remove(sprite);
      sprite.material.map.dispose();  // Dispose canvas texture
      sprite.material.dispose();
    });
    this.debugSprites = [];
  }
  
  renderSoldier(soldier) {
    const material = this.getTeamMaterial(soldier.team.color);
    const mesh = this.getMeshFromPool(this.soldierMeshPool, this.soldierGeometry, material);
    
    // Position (convert 2D to 3D coordinates)
    mesh.position.x = soldier.x - this.width / 2;
    mesh.position.y = -soldier.y + this.height / 2;
    mesh.position.z = THREE_RENDERER_CONFIG.Z_POSITIONS.SOLDIER; // High elevation for soldiers
    
    // Scale based on level and spawn animation
    const levelScale = 1 + (soldier.level - 1) * THREE_RENDERER_CONFIG.ANIMATIONS.SOLDIER.LEVEL_SCALE_MULTIPLIER;
    const spawnScale = soldier.opacity || 1; // Use opacity as scale factor (0-1)
    const finalScale = levelScale * spawnScale;
    mesh.scale.set(finalScale, finalScale, finalScale);
    
    // Berserker glow effect
    if (soldier.isBerserker) {
      mesh.material.emissive.set(soldier.team.color);
      mesh.material.emissiveIntensity = THREE_RENDERER_CONFIG.ANIMATIONS.BERSERKER.EMISSIVE_BASE + Math.sin(Date.now() * THREE_RENDERER_CONFIG.ANIMATIONS.BERSERKER.GLOW_SPEED) * THREE_RENDERER_CONFIG.ANIMATIONS.BERSERKER.GLOW_AMPLITUDE;
    }
    
    // Damage flash effect (overrides berserker if active)
    if (soldier.damageIndicator && soldier.damageIndicator > 0) {
      // Flash red when hit
      mesh.material.emissive.setHex(THREE_RENDERER_CONFIG.ANIMATIONS.DAMAGE.FLASH_COLOR);
      mesh.material.emissiveIntensity = soldier.damageIndicator * THREE_RENDERER_CONFIG.ANIMATIONS.DAMAGE.FLASH_INTENSITY;
    }
    
    // Point soldier in facing direction
    mesh.rotation.z = -soldier.facingAngle + Math.PI / 2; // Adjust for model orientation
    
    // Small wobble for visual interest
    mesh.rotation.x = Math.sin(Date.now() * THREE_RENDERER_CONFIG.ANIMATIONS.SOLDIER.WOBBLE_SPEED + soldier.id) * THREE_RENDERER_CONFIG.ANIMATIONS.SOLDIER.WOBBLE_AMPLITUDE;
  }
  
  renderBase(base) {
    // Get or create base group from pool
    let baseGroup = this.baseMeshPool.find(g => !g.visible);
    if (!baseGroup) {
      console.log('Creating new base group for', base.team.name);
      baseGroup = new THREE.Group();
      
      // Create outer pipe walls with transparency
      const pipeMaterial = this.getTeamMaterial(base.team.color).clone();
      pipeMaterial.transparent = true;
      pipeMaterial.opacity = THREE_RENDERER_CONFIG.MATERIALS.BASE.PIPE_OPACITY; // Make column transparent
      const outerPipeMesh = new THREE.Mesh(this.baseOuterPipeGeometry, pipeMaterial);
      outerPipeMesh.castShadow = true;
      outerPipeMesh.receiveShadow = true;
      outerPipeMesh.rotation.x = Math.PI / 2; // Rotate to stand upright
      outerPipeMesh.position.z = THREE_RENDERER_CONFIG.Z_POSITIONS.BASE_PIPE_CENTER; // Center vertically
      
      // Create inner pipe walls (inverted to create hollow effect)
      const innerMaterial = pipeMaterial.clone();
      innerMaterial.side = THREE.BackSide; // Render inside faces
      const innerPipeMesh = new THREE.Mesh(this.baseInnerPipeGeometry, innerMaterial);
      innerPipeMesh.castShadow = false; // Inner pipe doesn't need to cast shadow
      innerPipeMesh.receiveShadow = true;
      innerPipeMesh.rotation.x = Math.PI / 2; // Rotate to stand upright
      innerPipeMesh.position.z = THREE_RENDERER_CONFIG.Z_POSITIONS.BASE_PIPE_CENTER; // Same height as outer
      
      // Create ring at top with opaque team color
      const ringMaterial = this.getTeamMaterial(base.team.color).clone();
      ringMaterial.transparent = false; // Ring is opaque
      ringMaterial.opacity = 1.0;
      const ringMesh = new THREE.Mesh(this.baseRingGeometry, ringMaterial);
      ringMesh.castShadow = true;
      ringMesh.receiveShadow = true;
      // No rotation needed - ring is already in XY plane
      ringMesh.position.z = THREE_RENDERER_CONFIG.Z_POSITIONS.BASE_RING; // Top of the pipes
      
      // Create floor disk with transparency
      const floorMaterial = this.getTeamMaterial(base.team.color).clone();
      floorMaterial.transparent = true;
      floorMaterial.opacity = THREE_RENDERER_CONFIG.MATERIALS.BASE.FLOOR_OPACITY; // 25% transparency as requested
      floorMaterial.side = THREE.DoubleSide;
      const floorMesh = new THREE.Mesh(this.baseFloorGeometry, floorMaterial);
      floorMesh.castShadow = false; // Floor doesn't need to cast shadow
      floorMesh.receiveShadow = true;
      floorMesh.rotation.x = -Math.PI / 2; // Lay flat
      floorMesh.position.z = THREE_RENDERER_CONFIG.Z_POSITIONS.BASE_FLOOR; // Bottom of the pipe
      
      // Add meshes to group
      baseGroup.add(outerPipeMesh);
      // Don't add inner pipe - just use ring to show hollow effect
      baseGroup.add(ringMesh);
      baseGroup.add(floorMesh);
      baseGroup.userData = { outerPipeMesh, innerPipeMesh, ringMesh, floorMesh };
      
      this.baseMeshPool.push(baseGroup);
      this.scene.add(baseGroup);
    }
    
    baseGroup.visible = true;
    
    // Position the group
    baseGroup.position.x = base.x - this.width / 2;
    baseGroup.position.y = -base.y + this.height / 2;
    baseGroup.position.z = 0; // At ground level
    
    // Apply spawn scale (same as 2D)
    const currentScale = base.isSpawning ? base.spawnScale : 1.0;
    
    // Base shrinks when damaged AND applies spawn scale
    const healthRatio = base.health / base.maxHealth;
    const drawSize = base.size * healthRatio * currentScale;
    
    // Apply delivery pulse (grows base slightly)
    const pulseScale = 1.0 + (base.deliveryPulse * THREE_RENDERER_CONFIG.ANIMATIONS.BASE.DELIVERY_PULSE_SCALE); // Max 20% size increase
    const finalSize = drawSize * pulseScale;
    
    // Set the final scale
    baseGroup.scale.set(finalSize, finalSize, finalSize);
    
    // Update materials for damage effects
    const { outerPipeMesh, innerPipeMesh, ringMesh, floorMesh } = baseGroup.userData;
    
    // Transparency for damaged bases (walls get more transparent when damaged)
    const wallOpacity = THREE_RENDERER_CONFIG.MATERIALS.BASE.DAMAGED_WALL_OPACITY * healthRatio; // Start at 0.3 opacity, decrease with damage
    outerPipeMesh.material.opacity = Math.max(THREE_RENDERER_CONFIG.MATERIALS.BASE.MIN_WALL_OPACITY, wallOpacity);
    innerPipeMesh.material.opacity = Math.max(THREE_RENDERER_CONFIG.MATERIALS.BASE.MIN_WALL_OPACITY, wallOpacity);
    // Ring stays opaque but can fade slightly when very damaged
    ringMesh.material.opacity = Math.max(THREE_RENDERER_CONFIG.MATERIALS.BASE.MIN_RING_OPACITY, healthRatio);
    
    // Floor always stays at 25% opacity
    floorMesh.material.opacity = THREE_RENDERER_CONFIG.MATERIALS.BASE.FLOOR_OPACITY;
    
    // Damage flash effect
    if (base.damageIndicator && base.damageIndicator > 0) {
      // Flash red when hit
      const flashIntensity = base.damageIndicator * 0.5;
      outerPipeMesh.material.emissive.setHex(THREE_RENDERER_CONFIG.ANIMATIONS.DAMAGE.FLASH_COLOR);
      outerPipeMesh.material.emissiveIntensity = flashIntensity;
      innerPipeMesh.material.emissive.setHex(THREE_RENDERER_CONFIG.ANIMATIONS.DAMAGE.FLASH_COLOR);
      innerPipeMesh.material.emissiveIntensity = flashIntensity;
      ringMesh.material.emissive.setHex(THREE_RENDERER_CONFIG.ANIMATIONS.DAMAGE.FLASH_COLOR);
      ringMesh.material.emissiveIntensity = flashIntensity;
    } else {
      // Reset emissive
      outerPipeMesh.material.emissive.setHex(THREE_RENDERER_CONFIG.MATERIALS.TEAM.EMISSIVE_BLACK);
      outerPipeMesh.material.emissiveIntensity = 0;
      innerPipeMesh.material.emissive.setHex(THREE_RENDERER_CONFIG.MATERIALS.TEAM.EMISSIVE_BLACK);
      innerPipeMesh.material.emissiveIntensity = 0;
      ringMesh.material.emissive.setHex(THREE_RENDERER_CONFIG.MATERIALS.TEAM.EMISSIVE_BLACK);
      ringMesh.material.emissiveIntensity = 0;
    }
  }
  
  renderProjectile(projectile) {
    // Check if this is a base missile
    const isMissile = projectile.isPerfectHoming;
    
    if (isMissile) {
      // Render as a larger missile with effects
      // Get the team color from the projectile's owner
      const teamColor = projectile.owner && projectile.owner.team ? projectile.owner.team.color : projectile.color || THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.COLOR_YELLOW;
      const material = new THREE.MeshStandardMaterial({
        color: teamColor,
        emissive: THREE_RENDERER_CONFIG.MATERIALS.TEAM.EMISSIVE_BLACK,  // No base emissive
        metalness: 0.7,
        roughness: 0.3
      });
      
      const mesh = this.getMeshFromPool(this.missileMeshPool, this.missileGeometry, material);
      
      // Missiles should not cast shadows (only their light should)
      mesh.castShadow = false;
      
      // Position
      mesh.position.x = projectile.x - this.width / 2;
      mesh.position.y = -projectile.y + this.height / 2;
      mesh.position.z = THREE_RENDERER_CONFIG.Z_POSITIONS.MISSILE; // Higher than regular projectiles
      
      // Rotation - cone points forward
      const angle = Math.atan2(projectile.vy, projectile.vx);
      mesh.rotation.z = -angle - Math.PI / 2;
      
      // Scale for impact
      const scale = PROJECTILE_CONFIG.MISSILE_SIZE_MULTIPLIER;
      mesh.scale.set(scale, scale, scale);
      
      // Add missile glow matching team color
      mesh.material.emissive.set(teamColor);
      mesh.material.emissiveIntensity = 0.5;
      
      // Create or update missile light
      let light = this.missileLights.get(projectile);
      if (!light) {
        // Create point light for missile matching team color
        light = new THREE.PointLight(teamColor, THREE_RENDERER_CONFIG.LIGHTING.MISSILE.INTENSITY, THREE_RENDERER_CONFIG.LIGHTING.MISSILE.DISTANCE); // Very bright light
        light.castShadow = true; // Enable shadows
        // Configure shadow properties
        light.shadow.mapSize.width = THREE_RENDERER_CONFIG.LIGHTING.MISSILE.SHADOW_MAP_SIZE;
        light.shadow.mapSize.height = THREE_RENDERER_CONFIG.LIGHTING.MISSILE.SHADOW_MAP_SIZE;
        light.shadow.camera.near = THREE_RENDERER_CONFIG.LIGHTING.MISSILE.SHADOW_NEAR;
        light.shadow.camera.far = THREE_RENDERER_CONFIG.LIGHTING.MISSILE.SHADOW_FAR;
        this.scene.add(light);
        this.missileLights.set(projectile, light);
      }
      
      // Update light position
      light.position.x = mesh.position.x;
      light.position.y = mesh.position.y;
      light.position.z = mesh.position.z + THREE_RENDERER_CONFIG.LIGHTING.MISSILE.Z_OFFSET; // Just above the missile
      
      // Check for explosion
      if (projectile.exploding) {
        // Hide the missile mesh during explosion
        mesh.visible = false;
        
        // Explosion light effect
        const explosionProgress = projectile.explosionTimer / PROJECTILE_CONFIG.MISSILE_EXPLOSION_DURATION;
        const intensity = (1 - explosionProgress) * THREE_RENDERER_CONFIG.LIGHTING.EXPLOSION.MAX_INTENSITY; // Very bright explosion
        light.intensity = intensity;
        light.color.setHex(THREE_RENDERER_CONFIG.LIGHTING.EXPLOSION.COLOR); // Yellow explosion
        light.distance = THREE_RENDERER_CONFIG.LIGHTING.EXPLOSION.BASE_DISTANCE + (1 - explosionProgress) * THREE_RENDERER_CONFIG.LIGHTING.EXPLOSION.EXPANSION_DISTANCE; // Large light radius
        
        // Create explosion particles on first frame
        if (projectile.explosionTimer === 1) {
          this.createExplosion(projectile);
        }
        
        // Clean up light when explosion ends
        if (explosionProgress >= 1) {
          this.scene.remove(light);
          light.dispose(); // Properly dispose
          this.missileLights.delete(projectile);
        }
      } else {
        // Normal flight - bright team-colored glow with pulsing
        const pulse = 1 + Math.sin(Date.now() * 0.01) * THREE_RENDERER_CONFIG.LIGHTING.MISSILE.PULSE_AMPLITUDE;
        light.intensity = THREE_RENDERER_CONFIG.LIGHTING.MISSILE.INTENSITY * pulse;
        light.color.set(teamColor);
        light.distance = THREE_RENDERER_CONFIG.LIGHTING.MISSILE.DISTANCE;
        
        // Add exhaust particles
        this.createExhaustParticles(projectile);
      }
    } else {
      // Regular projectile
      const material = this.getTeamMaterial(projectile.color || '#fff');
      const mesh = this.getMeshFromPool(this.projectileMeshPool, this.projectileGeometry, material);
      
      // Check for explosion state
      if (projectile.exploding) {
        // Hide the projectile mesh during explosion
        mesh.visible = false;
        
        // Create small explosion effect (10% of missile size)
        if (projectile.explosionTimer === 1) {
          this.createSmallExplosion(projectile);
        }
      } else {
        // Normal projectile rendering
        // Position
        mesh.position.x = projectile.x - this.width / 2;
        mesh.position.y = -projectile.y + this.height / 2;
        mesh.position.z = THREE_RENDERER_CONFIG.Z_POSITIONS.PROJECTILE; // Slightly above other entities
        
        // Rotation to match direction
        const angle = Math.atan2(projectile.vy, projectile.vx);
        mesh.rotation.x = Math.PI / 2;
        mesh.rotation.y = angle;
        
        // Reset emissive
        mesh.material.emissive = mesh.material.color;
        mesh.material.emissiveIntensity = THREE_RENDERER_CONFIG.ANIMATIONS.PROJECTILE.EMISSIVE_INTENSITY;
      }
    }
  }
  
  createExhaustParticles(missile) {
    // Get team color for exhaust particles
    const teamColor = missile.owner && missile.owner.team ? missile.owner.team.color : 0xff6600;
    
    // Create exhaust trail particles
    for (let i = 0; i < THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.PER_FRAME; i++) {
      const particleGeometry = new THREE.SphereGeometry(THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.GEOMETRY.radius, THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.GEOMETRY.widthSegments, THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.GEOMETRY.heightSegments);
      const particleMaterial = new THREE.MeshBasicMaterial({
        color: teamColor,
        transparent: true,
        opacity: THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.OPACITY
      });
      
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      
      // Calculate missile velocity direction
      const speed = Math.sqrt(missile.vx * missile.vx + missile.vy * missile.vy);
      if (speed === 0) return; // Skip if missile not moving
      
      const dirX = missile.vx / speed;
      const dirY = missile.vy / speed;
      
      // Position at rear of missile
      const missileLength = THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.MISSILE_LENGTH; // Half the cone length
      const spread = THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.SPREAD;
      
      // Convert missile position to 3D coordinates
      const missile3DX = missile.x - this.width / 2;
      const missile3DY = -(missile.y - this.height / 2);
      
      // Place particle at rear of missile
      particle.position.x = missile3DX - dirX * missileLength + (Math.random() - 0.5) * spread;
      particle.position.y = missile3DY + dirY * missileLength + (Math.random() - 0.5) * spread;
      particle.position.z = 7;
      
      // Store particle data - particles move opposite to missile direction
      particle.userData = {
        velocity: {
          x: -dirX * THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.VELOCITY_MULTIPLIER + (Math.random() - 0.5) * THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.VELOCITY_RANDOM,
          y: dirY * THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.VELOCITY_MULTIPLIER + (Math.random() - 0.5) * THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.VELOCITY_RANDOM  // Note: positive Y because of coordinate system
        },
        life: 1.0,
        decay: THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.DECAY_RATE
      };
      
      this.scene.add(particle);
      this.exhaustParticles.push(particle);
    }
  }
  
  createExplosion(projectile) {
    // Create explosion effect when missile hits
    const numParticles = THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.PARTICLE_COUNT;
    
    for (let i = 0; i < numParticles; i++) {
      const particleGeometry = new THREE.SphereGeometry(THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.PARTICLE_GEOMETRY.radius, THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.PARTICLE_GEOMETRY.widthSegments, THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.PARTICLE_GEOMETRY.heightSegments);
      const particleMaterial = new THREE.MeshBasicMaterial({
        color: i < THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.YELLOW_THRESHOLD ? THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.COLOR_YELLOW : THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.COLOR_ORANGE, // Yellow and orange
        transparent: true,
        opacity: 1.0
      });
      
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      
      // Position at explosion center
      particle.position.x = projectile.explosionX - this.width / 2;
      particle.position.y = -(projectile.explosionY - this.height / 2);
      particle.position.z = THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.Z_POSITION; // Above other entities
      
      // Random velocity in all directions
      const speed = THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SPEED_MIN + Math.random() * (THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SPEED_MAX - THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SPEED_MIN);
      const angle = Math.random() * Math.PI * 2;
      const zVel = Math.random() * THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.Z_VELOCITY_MAX;
      
      // Store particle data
      particle.userData = {
        velocity: {
          x: Math.cos(angle) * speed,
          y: Math.sin(angle) * speed,
          z: zVel
        },
        life: 1.0,
        decay: THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.DECAY_RATE, // Faster decay for explosion
        scale: THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SCALE_MIN + Math.random() * THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SCALE_MAX
      };
      
      this.scene.add(particle);
      this.explosionParticles.push(particle);
    }
    
    // Create shockwave ring
    const ringGeometry = new THREE.RingGeometry(THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SHOCKWAVE.GEOMETRY.innerRadius, THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SHOCKWAVE.GEOMETRY.outerRadius, THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SHOCKWAVE.GEOMETRY.segments);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.COLOR_YELLOW,
      transparent: true,
      opacity: THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SHOCKWAVE.OPACITY,
      side: THREE.DoubleSide
    });
    
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.x = projectile.explosionX - this.width / 2;
    ring.position.y = -(projectile.explosionY - this.height / 2);
    ring.position.z = THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SHOCKWAVE.Z_POSITION;
    
    ring.userData = {
      velocity: { x: 0, y: 0, z: 0 },
      life: 1.0,
      decay: THREE_RENDERER_CONFIG.EXPLOSIONS.MISSILE.SHOCKWAVE.DECAY_RATE,
      scale: 1,
      isRing: true
    };
    
    this.scene.add(ring);
    this.explosionParticles.push(ring);
  }
  
  createUnitExplosion(unit) {
    // Create explosion effect when unit dies - 50% larger than projectile explosion
    const numParticles = THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.PARTICLE_COUNT; // More particles for unit death
    
    // Convert unit position to 3D coordinates
    const explosionX = unit.x - this.width / 2;
    const explosionY = -(unit.y - this.height / 2);
    const explosionZ = THREE_RENDERER_CONFIG.Z_POSITIONS.SOLDIER; // Same height as units
    
    for (let i = 0; i < numParticles; i++) {
      const particleGeometry = new THREE.SphereGeometry(THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.PARTICLE_GEOMETRY.radius, THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.PARTICLE_GEOMETRY.widthSegments, THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.PARTICLE_GEOMETRY.heightSegments); // 50% larger particles
      const particleMaterial = new THREE.MeshStandardMaterial({
        color: unit.team.color || 0xffff00,
        transparent: true,
        opacity: 1.0,
        emissive: unit.team.color || 0xffff00,
        emissiveIntensity: 0.5,
        roughness: 0.5,
        metalness: 0
      });
      
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      
      // Position at unit center
      particle.position.x = explosionX;
      particle.position.y = explosionY;
      particle.position.z = explosionZ;
      
      // Random velocity in all directions - 50% slower
      const speed = THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.SPEED_MIN + Math.random() * THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.SPEED_MAX; // Slower particles
      const angle = Math.random() * Math.PI * 2;
      const zVel = Math.random() * THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.Z_VELOCITY_RANGE + THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.Z_VELOCITY_MIN; // Less vertical movement
      
      // Store particle data
      particle.userData = {
        velocity: {
          x: Math.cos(angle) * speed,
          y: Math.sin(angle) * speed,
          z: zVel
        },
        life: 1.0,
        decay: THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.DECAY_RATE, // 50% slower decay for longer-lasting explosion
        scale: THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.SCALE_MIN + Math.random() * THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.SCALE_RANGE
      };
      
      this.scene.add(particle);
      this.explosionParticles.push(particle);
    }
    
    // Warm fire-like flash effect - larger for units
    const flashGeometry = new THREE.SphereGeometry(THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.FLASH.GEOMETRY.radius, THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.FLASH.GEOMETRY.widthSegments, THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.FLASH.GEOMETRY.heightSegments); // 50% larger flash
    const flashMaterial = new THREE.MeshStandardMaterial({
      color: THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.FLASH.COLOR, // Orange fire color
      transparent: true,
      opacity: THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.FLASH.OPACITY,
      emissive: THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.FLASH.EMISSIVE, // Warm orange emissive
      emissiveIntensity: THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.FLASH.INTENSITY,
      roughness: 0.5,
      metalness: 0
    });
    
    const flash = new THREE.Mesh(flashGeometry, flashMaterial);
    flash.position.x = explosionX;
    flash.position.y = explosionY;
    flash.position.z = explosionZ;
    
    flash.userData = {
      velocity: { x: 0, y: 0, z: 0 },
      life: 1.0,
      decay: THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.FLASH.DECAY, // 50% slower decay
      scale: THREE_RENDERER_CONFIG.EXPLOSIONS.UNIT.FLASH.SCALE,
      isFlash: true
    };
    
    this.scene.add(flash);
    this.explosionParticles.push(flash);
  }
  
  createSmallExplosion(projectile) {
    // Create small explosion effect for regular projectiles (10% of missile size)
    const numParticles = THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.PARTICLE_COUNT; // More particles for visibility
    
    for (let i = 0; i < numParticles; i++) {
      const particleGeometry = new THREE.SphereGeometry(THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.PARTICLE_GEOMETRY.radius, THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.PARTICLE_GEOMETRY.widthSegments, THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.PARTICLE_GEOMETRY.heightSegments); // Bigger particles
      const particleMaterial = new THREE.MeshStandardMaterial({
        color: projectile.color || 0xffff00,
        transparent: true,
        opacity: 1.0,
        emissive: projectile.color || 0xffff00,
        emissiveIntensity: 0.5,
        roughness: 0.5,
        metalness: 0
      });
      
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      
      // Position at explosion center
      particle.position.x = projectile.explosionX - this.width / 2;
      particle.position.y = -(projectile.explosionY - this.height / 2);
      particle.position.z = THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.Z_POSITION; // Just above projectiles
      
      // Random velocity in all directions
      const speed = THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.SPEED_MIN + Math.random() * (THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.SPEED_MAX - THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.SPEED_MIN); // Faster particles
      const angle = Math.random() * Math.PI * 2;
      const zVel = Math.random() * THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.Z_VELOCITY_MAX;
      
      // Store particle data
      particle.userData = {
        velocity: {
          x: Math.cos(angle) * speed,
          y: Math.sin(angle) * speed,
          z: zVel
        },
        life: 1.0,
        decay: THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.DECAY_RATE, // Faster decay for smaller explosion
        scale: THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.SCALE_MIN + Math.random() * THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.SCALE_RANGE
      };
      
      this.scene.add(particle);
      this.explosionParticles.push(particle);
    }
    
    // Bright flash effect
    const flashGeometry = new THREE.SphereGeometry(THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.FLASH.GEOMETRY.radius, THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.FLASH.GEOMETRY.widthSegments, THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.FLASH.GEOMETRY.heightSegments);
    const flashMaterial = new THREE.MeshStandardMaterial({
      color: THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.FLASH.COLOR,
      transparent: true,
      opacity: THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.FLASH.OPACITY,
      emissive: THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.FLASH.COLOR,
      emissiveIntensity: THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.FLASH.INTENSITY,
      roughness: 0.5,
      metalness: 0
    });
    
    const flash = new THREE.Mesh(flashGeometry, flashMaterial);
    flash.position.x = projectile.explosionX - this.width / 2;
    flash.position.y = -(projectile.explosionY - this.height / 2);
    flash.position.z = THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.FLASH.Z_POSITION;
    
    flash.userData = {
      velocity: { x: 0, y: 0, z: 0 },
      life: 1.0,
      decay: THREE_RENDERER_CONFIG.EXPLOSIONS.PROJECTILE.FLASH.DECAY, // Very fast decay
      scale: 1.0,
      isFlash: true
    };
    
    this.scene.add(flash);
    this.explosionParticles.push(flash);
  }
  
  updateParticles() {
    // Update exhaust particles
    for (let i = this.exhaustParticles.length - 1; i >= 0; i--) {
      const particle = this.exhaustParticles[i];
      const data = particle.userData;
      
      // Update position
      particle.position.x += data.velocity.x;
      particle.position.y += data.velocity.y;
      
      // Update life
      data.life -= data.decay;
      particle.material.opacity = data.life * THREE_RENDERER_CONFIG.PARTICLES.EXHAUST.OPACITY_MULTIPLIER;
      particle.scale.setScalar(data.life);
      
      // Remove dead particles
      if (data.life <= 0) {
        this.scene.remove(particle);
        particle.geometry.dispose();
        particle.material.dispose();
        this.exhaustParticles.splice(i, 1);
      }
    }
    
    // Update explosion particles (for future use)
    for (let i = this.explosionParticles.length - 1; i >= 0; i--) {
      const particle = this.explosionParticles[i];
      const data = particle.userData;
      
      // Update position
      particle.position.x += data.velocity.x;
      particle.position.y += data.velocity.y;
      particle.position.z += data.velocity.z;
      
      // Update life
      data.life -= data.decay;
      particle.material.opacity = data.life;
      
      // Handle different particle types
      if (data.isRing) {
        // Expand the ring
        const expansion = (1 - data.life) * PROJECTILE_CONFIG.MISSILE_EXPLOSION_RADIUS;
        particle.scale.setScalar(expansion);
        particle.material.opacity = data.life * THREE_RENDERER_CONFIG.MISC.RING_OPACITY_MULTIPLIER;
      } else if (data.isFlash) {
        // Expand and fade flash quickly
        const expansion = (1 - data.life) * 5;
        particle.scale.setScalar(expansion * data.scale);
        particle.material.opacity = data.life * THREE_RENDERER_CONFIG.MISC.FLASH_OPACITY_MULTIPLIER;
      } else {
        // Regular explosion particles
        particle.scale.setScalar(data.life * data.scale * THREE_RENDERER_CONFIG.MISC.EXPLOSION_PARTICLE_SCALE_MULTIPLIER);
      }
      
      // Remove dead particles
      if (data.life <= 0) {
        this.scene.remove(particle);
        particle.geometry.dispose();
        particle.material.dispose();
        this.explosionParticles.splice(i, 1);
      }
    }
  }
  
  createNebulaParticles(nebula) {
    const particles = [];
    const particleGeometry = new THREE.BoxGeometry(THREE_RENDERER_CONFIG.PARTICLES.NEBULA.GEOMETRY_SIZE, THREE_RENDERER_CONFIG.PARTICLES.NEBULA.GEOMETRY_SIZE, THREE_RENDERER_CONFIG.PARTICLES.NEBULA.GEOMETRY_SIZE); // Small cube particles
    
    // Create particles based on config
    for (let i = 0; i < THREE_RENDERER_CONFIG.PARTICLES.NEBULA.COUNT; i++) { // NEBULA_PARTICLE_COUNT for coverage
      const particleMaterial = new THREE.MeshBasicMaterial({
        color: THREE_RENDERER_CONFIG.PARTICLES.NEBULA.OPACITY_COLOR, // Pink color
        transparent: true,
        opacity: 0.5
      });
      
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);
      
      // Initialize particle data
      particle.userData = {
        angle: Math.random() * Math.PI * 2,
        distance: Math.random() * THREE_RENDERER_CONFIG.PARTICLES.NEBULA.MAX_DISTANCE, // NEBULA_RADIUS * 1.5 for 50% more range
        speed: THREE_RENDERER_CONFIG.PARTICLES.NEBULA.SPEED_MIN + Math.random() * (THREE_RENDERER_CONFIG.PARTICLES.NEBULA.SPEED_MAX - THREE_RENDERER_CONFIG.PARTICLES.NEBULA.SPEED_MIN),
        size: THREE_RENDERER_CONFIG.PARTICLES.NEBULA.SIZE_MIN + Math.random() * THREE_RENDERER_CONFIG.PARTICLES.NEBULA.SIZE_MAX,
        baseOpacity: THREE_RENDERER_CONFIG.PARTICLES.NEBULA.OPACITY_MIN + Math.random() * (THREE_RENDERER_CONFIG.PARTICLES.NEBULA.OPACITY_MAX - THREE_RENDERER_CONFIG.PARTICLES.NEBULA.OPACITY_MIN)
      };
      
      this.scene.add(particle);
      particles.push(particle);
    }
    
    return particles;
  }
  
  updateNebulaParticles(nebula, particles, mesh) {
    particles.forEach(particle => {
      const data = particle.userData;
      
      // Update angle
      data.angle += data.speed * THREE_RENDERER_CONFIG.PARTICLES.NEBULA.ANGLE_SPEED;
      data.distance = (data.distance + data.speed * THREE_RENDERER_CONFIG.PARTICLES.NEBULA.DISTANCE_SPEED) % THREE_RENDERER_CONFIG.PARTICLES.NEBULA.MAX_DISTANCE; // 50% more range
      
      // Calculate position relative to nebula center
      const x = Math.cos(data.angle) * data.distance;
      const y = Math.sin(data.angle) * data.distance;
      const z = THREE_RENDERER_CONFIG.PARTICLES.NEBULA.Z_MIN + Math.random() * THREE_RENDERER_CONFIG.PARTICLES.NEBULA.Z_MAX; // Particles float 2-7 units above the disk
      
      // Set world position
      particle.position.x = mesh.position.x + x;
      particle.position.y = mesh.position.y + y;
      particle.position.z = mesh.position.z + z;
      
      // Update opacity based on distance from center
      const fadeOpacity = 1 - (data.distance / THREE_RENDERER_CONFIG.PARTICLES.NEBULA.MAX_DISTANCE); // Adjusted for 50% more range
      particle.material.opacity = data.baseOpacity * fadeOpacity * (nebula.opacity || 1);
      
      // Scale based on size
      particle.scale.setScalar(data.size);
    });
  }
  
  createTextSprite(text, color = '#ffffff', scale = null) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.CANVAS_WIDTH;
    canvas.height = THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.CANVAS_HEIGHT;

    context.font = THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.FONT;
    context.fillStyle = color;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.CANVAS_WIDTH / 2, THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.CANVAS_HEIGHT / 2);

    const texture = new THREE.CanvasTexture(canvas);
    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      sizeAttenuation: false  // Critical for orthographic camera - sprites won't scale with distance
    });
    const sprite = new THREE.Sprite(spriteMaterial);

    // Use provided scale or default to canvas/4 (original working scale for nebula text)
    const defaultScale = {
      x: THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.CANVAS_WIDTH / 4,  // 64
      y: THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.CANVAS_HEIGHT / 4  // 32
    };

    const finalScale = scale || defaultScale;
    sprite.scale.set(
      finalScale.x,
      finalScale.y,
      1
    );

    return sprite;
  }
  
  renderResource(resource) {
    let geometry, material, pool;
    
    if (resource instanceof Metal) {
      geometry = this.metalGeometry;
      material = this.resourceMaterials.metal;
      pool = this.metalMeshPool;
    } else if (resource instanceof Nebula) {
      geometry = this.nebulaGeometry;
      material = this.resourceMaterials.nebula;
      pool = this.nebulaMeshPool;
    } else if (resource instanceof Crystal) {
      geometry = this.crystalGeometry;
      material = this.resourceMaterials.crystal;
      pool = this.crystalMeshPool;
    } else {
      // Unknown resource type, skip rendering
      console.warn(`Unknown resource type: ${resource.constructor.name}`);
      return;
    }
    
    const mesh = this.getMeshFromPool(pool, geometry, material);
    
    // Position
    mesh.position.x = resource.x - this.width / 2;
    mesh.position.y = -resource.y + this.height / 2;
    mesh.position.z = resource instanceof Nebula ? THREE_RENDERER_CONFIG.Z_POSITIONS.NEBULA : THREE_RENDERER_CONFIG.Z_POSITIONS.RESOURCE; // Nebulas sit low, other resources float higher
    
    // Rotation animation and special effects
    if (resource instanceof Metal) {
      // Initialize random rotation properties on the resource itself if not set
      if (resource.rotationOffset === undefined) {
        resource.rotationOffset = {
          x: Math.random() * Math.PI * 2,
          y: Math.random() * Math.PI * 2,
          z: Math.random() * Math.PI * 2
        };
        resource.rotationSpeed = {
          x: (Math.random() - 0.5) * THREE_RENDERER_CONFIG.ANIMATIONS.METAL.ROTATION_SPEED_RANGE, // Random speed and direction for each axis
          y: (Math.random() - 0.5) * THREE_RENDERER_CONFIG.ANIMATIONS.METAL.ROTATION_SPEED_RANGE,
          z: (Math.random() - 0.5) * THREE_RENDERER_CONFIG.ANIMATIONS.METAL.ROTATION_SPEED_RANGE
        };
      }
      // Apply rotation on all axes with different speeds
      const time = Date.now();
      mesh.rotation.x = time * resource.rotationSpeed.x + resource.rotationOffset.x;
      mesh.rotation.y = time * resource.rotationSpeed.y + resource.rotationOffset.y;
      mesh.rotation.z = time * resource.rotationSpeed.z + resource.rotationOffset.z;
    } else if (resource instanceof Crystal) {
      mesh.rotation.y = Date.now() * THREE_RENDERER_CONFIG.ANIMATIONS.CRYSTAL.ROTATION_SPEED;
      mesh.rotation.z = Math.sin(Date.now() * THREE_RENDERER_CONFIG.ANIMATIONS.CRYSTAL.ROTATION_SPEED) * THREE_RENDERER_CONFIG.ANIMATIONS.CRYSTAL.WOBBLE_AMPLITUDE;
      // Add crystal glow
      mesh.material.emissive.setHex(0x00ffff);
      mesh.material.emissiveIntensity = THREE_RENDERER_CONFIG.ANIMATIONS.CRYSTAL.GLOW_BASE + Math.sin(Date.now() * THREE_RENDERER_CONFIG.ANIMATIONS.CRYSTAL.GLOW_SPEED) * THREE_RENDERER_CONFIG.ANIMATIONS.CRYSTAL.GLOW_AMPLITUDE;
    } else if (resource instanceof Nebula) {
      // Nebulas shouldn't cast shadows - they're clouds
      mesh.castShadow = false;
      
      // Update nebula opacity based on depletion
      mesh.material.opacity = resource.opacity || 1;
      
      // Add nebula glow (slowed by 50%)
      mesh.material.emissive.setHex(0xff00ff);
      mesh.material.emissiveIntensity = THREE_RENDERER_CONFIG.ANIMATIONS.NEBULA.GLOW_BASE + Math.sin(Date.now() * THREE_RENDERER_CONFIG.ANIMATIONS.NEBULA.GLOW_SPEED) * THREE_RENDERER_CONFIG.ANIMATIONS.NEBULA.GLOW_AMPLITUDE;
      
      // Create or update particle system
      let particles = this.nebulaParticles.get(resource);
      if (!particles) {
        particles = this.createNebulaParticles(resource);
        this.nebulaParticles.set(resource, particles);
      }
      
      // Update particle positions
      this.updateNebulaParticles(resource, particles, mesh);
      
      // Create or update text sprites
      let textSprites = this.nebulaTextSprites.get(resource);
      if (!textSprites) {
        textSprites = {
          gathererSprite: null,
          percentSprite: null
        };
        this.nebulaTextSprites.set(resource, textSprites);
      }
      
      // Update gatherer count text
      if (resource.gatherers && (resource.gatherers.size > 0 || resource.isDepleted)) {
        const gathererText = `${resource.gatherers.size}/${THREE_RENDERER_CONFIG.UI.NEBULA_THRESHOLDS.MAX_GATHERERS}`; // NEBULA_MAX_GATHERERS
        if (!textSprites.gathererSprite) {
          textSprites.gathererSprite = this.createTextSprite(gathererText, '#ffffff');
          this.scene.add(textSprites.gathererSprite);
        } else {
          // Update text by recreating sprite (Three.js doesn't support dynamic text update easily)
          this.scene.remove(textSprites.gathererSprite);
          textSprites.gathererSprite.material.dispose();
          textSprites.gathererSprite = this.createTextSprite(gathererText, '#ffffff');
          this.scene.add(textSprites.gathererSprite);
        }
        textSprites.gathererSprite.position.x = mesh.position.x;
        textSprites.gathererSprite.position.y = mesh.position.y + THREE_RENDERER_CONFIG.UI.TEXT_OFFSETS.GATHERER_Y;  // Above the nebula
        textSprites.gathererSprite.position.z = mesh.position.z + THREE_RENDERER_CONFIG.UI.TEXT_OFFSETS.Z;
      }
      
      // Update percentage text
      const percentRemaining = Math.floor((resource.remainingOrganics / resource.totalOrganics) * 100);
      const percentColor = percentRemaining > THREE_RENDERER_CONFIG.UI.NEBULA_THRESHOLDS.HIGH ? THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.COLOR_GREEN : 
                          percentRemaining > THREE_RENDERER_CONFIG.UI.NEBULA_THRESHOLDS.MID ? THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.COLOR_YELLOW : THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.COLOR_RED;
      const percentText = `${percentRemaining}%`;
      
      if (!textSprites.percentSprite) {
        textSprites.percentSprite = this.createTextSprite(percentText, percentColor);
        this.scene.add(textSprites.percentSprite);
      } else {
        // Update text
        this.scene.remove(textSprites.percentSprite);
        textSprites.percentSprite.material.dispose();
        textSprites.percentSprite = this.createTextSprite(percentText,
          resource.isDepleted ? THREE_RENDERER_CONFIG.UI.TEXT_SPRITE.COLOR_DEPLETED : percentColor);
        this.scene.add(textSprites.percentSprite);
      }
      textSprites.percentSprite.position.x = mesh.position.x;
      textSprites.percentSprite.position.y = mesh.position.y + THREE_RENDERER_CONFIG.UI.TEXT_OFFSETS.PERCENT_Y;  // Higher than gatherer text
      textSprites.percentSprite.position.z = mesh.position.z + THREE_RENDERER_CONFIG.UI.TEXT_OFFSETS.Z;
      
      // Clean up if nebula is inactive
      if (!resource.active) {
        if (particles) {
          particles.forEach(particle => {
            this.scene.remove(particle);
            particle.geometry.dispose();
            particle.material.dispose();
          });
          this.nebulaParticles.delete(resource);
        }
        
        if (textSprites.gathererSprite) {
          this.scene.remove(textSprites.gathererSprite);
          textSprites.gathererSprite.material.dispose();
        }
        if (textSprites.percentSprite) {
          this.scene.remove(textSprites.percentSprite);
          textSprites.percentSprite.material.dispose();
        }
        this.nebulaTextSprites.delete(resource);
      }
    }
    
    // Pulse effect (slowed by 50%)
    const pulse = Math.sin(Date.now() * THREE_RENDERER_CONFIG.ANIMATIONS.RESOURCE.PULSE_SPEED + resource.x) * THREE_RENDERER_CONFIG.ANIMATIONS.RESOURCE.PULSE_AMPLITUDE + THREE_RENDERER_CONFIG.ANIMATIONS.RESOURCE.PULSE_BASE;
    // Don't apply pulse to nebulas - they look better static
    if (!(resource instanceof Nebula)) {
      mesh.scale.set(pulse, pulse, pulse);
    }
  }
  
  renderCorpse(corpse) {
    // Create dark gray corpse marker
    const material = new THREE.MeshStandardMaterial({ 
      color: THREE_RENDERER_CONFIG.MATERIALS.CORPSE.COLOR,
      transparent: true,
      opacity: corpse.opacity || 1.0,
      roughness: THREE_RENDERER_CONFIG.MATERIALS.CORPSE.ROUGHNESS,
      metalness: THREE_RENDERER_CONFIG.MATERIALS.CORPSE.METALNESS
    });
    
    const mesh = this.getMeshFromPool(this.corpseMeshPool, this.corpseGeometry, material);
    
    // Position
    mesh.position.x = corpse.x - this.width / 2;
    mesh.position.y = -corpse.y + this.height / 2;
    mesh.position.z = THREE_RENDERER_CONFIG.Z_POSITIONS.CORPSE; // Just above ground
    
    // X shape rotation - rotate flat box to create X
    mesh.rotation.z = THREE_RENDERER_CONFIG.ANIMATIONS.CORPSE.ROTATION;
    
    // Pulse effect
    const pulse = Math.sin(Date.now() * THREE_RENDERER_CONFIG.ANIMATIONS.CORPSE.PULSE_SPEED + corpse.x) * THREE_RENDERER_CONFIG.ANIMATIONS.CORPSE.PULSE_AMPLITUDE + THREE_RENDERER_CONFIG.ANIMATIONS.CORPSE.PULSE_BASE;
    mesh.scale.set(pulse, pulse, 1);
  }

  renderTractorBeams(base) {
    // Clean up old tractor beam lines from previous frame
    if (!this.tractorBeamLines) {
      this.tractorBeamLines = [];
    }

    // Render resource tractor beams (Metal/Crystal)
    if (base.tractorBeamResources && base.tractorBeamResources.length > 0) {
      for (const resource of base.tractorBeamResources) {
        const pulsePhase = (Date.now() % 1000) / 1000; // 0-1 cycle per second
        const pulseAlpha = 0.3 + Math.sin(pulsePhase * Math.PI * 2) * 0.2; // Higher opacity

        // Create line from base to resource
        const points = [
          new THREE.Vector3(base.x - this.width / 2, -base.y + this.height / 2, 10),
          new THREE.Vector3(resource.x - this.width / 2, -resource.y + this.height / 2, 10)
        ];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);

        // Blue for metal, cyan for crystal
        const color = resource.constructor.name === 'Crystal' ? 0x00ffff : 0x66b3ff;

        // Use LineDashedMaterial for dashed effect (matches 2D renderer)
        const material = new THREE.LineDashedMaterial({
          color: color,
          transparent: true,
          opacity: pulseAlpha,
          dashSize: 8,
          gapSize: 4,
          linewidth: 1 // WebGL limitation - always renders as 1px anyway
        });

        const line = new THREE.Line(geometry, material);
        line.computeLineDistances(); // Required for dashed lines - call on Line, not geometry
        this.scene.add(line);
        this.tractorBeamLines.push(line);

        // Add glow sphere around resource
        const glowGeometry = new THREE.SphereGeometry(6, 8, 8);
        const glowMaterial = new THREE.MeshBasicMaterial({
          color: color,
          transparent: true,
          opacity: pulseAlpha * 0.5,
          wireframe: true
        });
        const glow = new THREE.Mesh(glowGeometry, glowMaterial);
        glow.position.set(resource.x - this.width / 2, -resource.y + this.height / 2, 10);
        this.scene.add(glow);
        this.tractorBeamLines.push(glow); // Store for cleanup
      }
    }

    // Render corpse tractor beams (soul harvesting)
    if (base.tractorBeamCorpses && base.tractorBeamCorpses.length > 0) {
      for (const corpse of base.tractorBeamCorpses) {
        const pulsePhase = (Date.now() % 1000) / 1000;
        const pulseAlpha = 0.2 + Math.sin(pulsePhase * Math.PI * 2) * 0.1;

        // Create line from base to corpse
        const points = [
          new THREE.Vector3(base.x - this.width / 2, -base.y + this.height / 2, 10),
          new THREE.Vector3(corpse.x - this.width / 2, -corpse.y + this.height / 2, 10)
        ];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);

        // Use LineDashedMaterial with different dash pattern for corpses
        const material = new THREE.LineDashedMaterial({
          color: 0xff66cc, // Pink for organics/souls
          transparent: true,
          opacity: pulseAlpha,
          dashSize: 10,
          gapSize: 5,
          linewidth: 1
        });

        const line = new THREE.Line(geometry, material);
        line.computeLineDistances(); // Required for dashed lines - call on Line, not geometry
        this.scene.add(line);
        this.tractorBeamLines.push(line);

        // Add glow sphere around corpse
        const glowGeometry = new THREE.SphereGeometry(8, 8, 8);
        const glowMaterial = new THREE.MeshBasicMaterial({
          color: 0xff66cc,
          transparent: true,
          opacity: pulseAlpha * 0.6,
          wireframe: true
        });
        const glow = new THREE.Mesh(glowGeometry, glowMaterial);
        glow.position.set(corpse.x - this.width / 2, -corpse.y + this.height / 2, 10);
        this.scene.add(glow);
        this.tractorBeamLines.push(glow); // Store for cleanup
      }
    }
  }

  renderEntities(soldiers, projectiles, corpses = [], bases = [], resources = []) {
    // Update cursor light position
    this.cursorLight.position.x = this.mouseX - this.width / 2;
    this.cursorLight.position.y = -(this.mouseY - this.height / 2);
    this.cursorLight.position.z = THREE_RENDERER_CONFIG.Z_POSITIONS.CURSOR_LIGHT; // Lower for better ground illumination
    
    // Make spotlight follow cursor
    this.spotlightTarget.position.x = this.cursorLight.position.x;
    this.spotlightTarget.position.y = this.cursorLight.position.y;
    this.spotlightTarget.position.z = 0;
    
    // Position spotlight above cursor
    this.spotlight.position.x = this.cursorLight.position.x;
    this.spotlight.position.y = this.cursorLight.position.y;
    this.spotlight.position.z = THREE_RENDERER_CONFIG.LIGHTING.SPOTLIGHT.FOLLOW_Z;
    
    // Cursor light helper disabled for better visibility
    // if (!this.cursorLightHelper) {
    //   const helperGeometry = new THREE.SphereGeometry(5, 8, 8);
    //   const helperMaterial = new THREE.MeshBasicMaterial({
    //     color: 0xffffff,
    //     transparent: true,
    //     opacity: 0.8
    //   });
    //   this.cursorLightHelper = new THREE.Mesh(helperGeometry, helperMaterial);
    //   this.scene.add(this.cursorLightHelper);
    // }
    // this.cursorLightHelper.position.copy(this.cursorLight.position);
    // this.cursorLightHelper.material.color.copy(this.cursorLight.color);
    
    // Render in correct order
    bases.forEach(base => {
      if (base.alive) this.renderBase(base);
    });

    // Render tractor beams for all bases
    bases.forEach(base => {
      if (base.alive) this.renderTractorBeams(base);
    });

    // Render all resources (including corpses)
    resources.forEach(resource => {
      if (resource instanceof Corpse) {
        this.renderCorpse(resource);
      } else {
        this.renderResource(resource);
      }
    });
    
    // Check for newly dead units and create explosions
    const currentUnits = new Set();
    soldiers.forEach(soldier => {
      if (soldier.alive && soldier.health > 0) {
        this.renderSoldier(soldier);
        currentUnits.add(soldier);
      } else if (this.previousUnits.has(soldier)) {
        // Unit just died - create explosion
        this.createUnitExplosion(soldier);
      }
    });
    
    // Update previous units for next frame
    this.previousUnits = currentUnits;
    
    projectiles.forEach(projectile => {
      if (projectile.active) this.renderProjectile(projectile);
    });

    // Note: Scene rendering moved to render() method to ensure debug visuals are included
  }
  
  /**
   * Render debug information overlays
   *
   * ✅ PARITY ACHIEVED: Now matches Canvas2D debug features
   *
   * Renders:
   * - ✅ Target lines (red lines from soldier to target)
   * - ✅ Predicted aim point markers (white X at lastPredictedX/Y)
   * - ✅ Off-screen prediction count text ("OS:N" in purple)
   * - ✅ Target ID text ("#5 -> #12" in yellow)
   * - ✅ Respects DEBUG_VISUALIZATION_CONFIG flags
   *
   * @param {Soldier[]} soldiers - All soldiers
   * @param {Projectile[]} projectiles - All projectiles
   * @param {TeamBase[]} bases - All bases
   * @returns {Object} Debug statistics
   */
  renderDebugInfo(soldiers, projectiles, bases = []) {
    const debugMode = isDebugMode();

    // Update animation state machine
    if (debugMode && this.debugAnimationState === 'hidden') {
      // Debug just turned on - start growing
      this.debugAnimationState = 'growing';
      this.debugAnimationProgress = 0;
    } else if (!debugMode && (this.debugAnimationState === 'visible' || this.debugAnimationState === 'growing')) {
      // Debug just turned off - start shrinking
      this.debugAnimationState = 'shrinking';
    }

    // Update animation progress
    if (this.debugAnimationState === 'growing') {
      this.debugAnimationProgress += this.debugAnimationSpeed;
      if (this.debugAnimationProgress >= 1) {
        this.debugAnimationProgress = 1;
        this.debugAnimationState = 'visible';
      }
    } else if (this.debugAnimationState === 'shrinking') {
      this.debugAnimationProgress -= this.debugAnimationSpeed;
      if (this.debugAnimationProgress <= 0) {
        this.debugAnimationProgress = 0;
        this.debugAnimationState = 'hidden';
        // Clear debug visuals after shrink animation completes
        this.clearDebugVisuals();
        return;
      }
    }

    // Clear and recreate debug visuals each frame (except when shrinking to completion)
    if (this.debugAnimationState !== 'hidden') {
      this.clearDebugVisuals();
    }

    if (!debugMode) return;

    let stats = {
      fighting: 0,
      highLevel: 0,
      maxLevel: 0,
      brokenTargeting: 0,
      projectiles: projectiles.length,
      bases: []
    };

    // Debug lines and markers for soldiers
    soldiers.forEach(soldier => {
      if (!soldier.alive || soldier.health <= 0) return;

      if (soldier.level >= UI_CONFIG.HIGH_LEVEL_THRESHOLD) stats.highLevel++;
      if (soldier.level >= UI_CONFIG.MAX_LEVEL_THRESHOLD) stats.maxLevel++;

      if (soldier.state === 'fighting' && !soldier.target) {
        stats.brokenTargeting++;
      }

      // Draw perception radius circle with animation
      if (DEBUG_VISUALIZATION_CONFIG.showPerceptionRadius && soldier.perceptionRange) {
        // Get per-entity animation progress with random delay and easing
        const entityProgress = this.updateEntityAnimation(soldier, this.debugAnimationProgress);

        const perceptionGeometry = new THREE.RingGeometry(
          soldier.perceptionRange - 1,  // Inner radius (slightly smaller for visible ring)
          soldier.perceptionRange,      // Outer radius
          64                             // Segments (smooth circle)
        );
        const perceptionMaterial = new THREE.MeshBasicMaterial({
          color: 0x00ffff,  // Cyan
          transparent: true,
          opacity: 0.3 * entityProgress,  // Fade with entity-specific animation
          side: THREE.DoubleSide
        });
        const perceptionCircle = new THREE.Mesh(perceptionGeometry, perceptionMaterial);
        perceptionCircle.position.set(
          soldier.x - this.width / 2,
          -(soldier.y - this.height / 2),
          5  // Below entities but above ground
        );
        // Scale circle based on entity-specific animation progress (grow/shrink from center)
        perceptionCircle.scale.setScalar(entityProgress);
        this.scene.add(perceptionCircle);
        this.debugLines.push(perceptionCircle);
      }

      // Draw weapon radius circle with animation
      if (DEBUG_VISUALIZATION_CONFIG.showWeaponRadius && soldier.weaponRange) {
        // Get per-entity animation progress with random delay and easing
        const entityProgress = this.updateEntityAnimation(soldier, this.debugAnimationProgress);

        const weaponGeometry = new THREE.RingGeometry(
          soldier.weaponRange - 1,  // Inner radius
          soldier.weaponRange,      // Outer radius
          64                         // Segments
        );
        const weaponMaterial = new THREE.MeshBasicMaterial({
          color: 0xff8800,  // Orange
          transparent: true,
          opacity: 0.3 * entityProgress,  // Fade with entity-specific animation
          side: THREE.DoubleSide
        });
        const weaponCircle = new THREE.Mesh(weaponGeometry, weaponMaterial);
        weaponCircle.position.set(
          soldier.x - this.width / 2,
          -(soldier.y - this.height / 2),
          5
        );
        // Scale circle based on entity-specific animation progress (grow/shrink from center)
        weaponCircle.scale.setScalar(entityProgress);
        this.scene.add(weaponCircle);
        this.debugLines.push(weaponCircle);
      }

      if (soldier.target && soldier.state === 'fighting') {
        stats.fighting++;

        // 1. Draw targeting line (only if enabled)
        if (DEBUG_VISUALIZATION_CONFIG.showTargetLines) {
          const material = new THREE.LineBasicMaterial({
            color: THREE_RENDERER_CONFIG.DEBUG.LINE_COLOR,
            opacity: THREE_RENDERER_CONFIG.DEBUG.LINE_OPACITY,
            transparent: true
          });
          const points = [
            new THREE.Vector3(soldier.x - this.width / 2, -(soldier.y - this.height / 2), 10),
            new THREE.Vector3(soldier.target.x - this.width / 2, -(soldier.target.y - this.height / 2), 10)
          ];
          const geometry = new THREE.BufferGeometry().setFromPoints(points);
          const line = new THREE.Line(geometry, material);
          this.scene.add(line);
          this.debugLines.push(line);
        }

        // 2. Draw predicted aim point marker (white X)
        if (DEBUG_VISUALIZATION_CONFIG.showPredictedPositions &&
            soldier.lastPredictedX !== 0 && soldier.lastPredictedY !== 0) {
          // Create two rectangles to form an X shape
          // Horizontal bar of X
          const horizGeometry = new THREE.BoxGeometry(
            UI_CONFIG.PREDICTION_MARKER_SIZE * 2,
            UI_CONFIG.PREDICTION_MARKER_HEIGHT,
            1
          );
          const horizMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: UI_CONFIG.PREDICTION_MARKER_ALPHA
          });
          const horizMarker = new THREE.Mesh(horizGeometry, horizMaterial);
          horizMarker.position.set(
            soldier.lastPredictedX - this.width / 2,
            -(soldier.lastPredictedY - this.height / 2),
            15  // Above other entities
          );
          this.scene.add(horizMarker);
          this.debugMarkers.push(horizMarker);

          // Vertical bar of X
          const vertGeometry = new THREE.BoxGeometry(
            UI_CONFIG.PREDICTION_MARKER_HEIGHT,
            UI_CONFIG.PREDICTION_MARKER_SIZE * 2,
            1
          );
          const vertMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: UI_CONFIG.PREDICTION_MARKER_ALPHA
          });
          const vertMarker = new THREE.Mesh(vertGeometry, vertMaterial);
          vertMarker.position.set(
            soldier.lastPredictedX - this.width / 2,
            -(soldier.lastPredictedY - this.height / 2),
            15
          );
          this.scene.add(vertMarker);
          this.debugMarkers.push(vertMarker);
        }

        // 3. Draw target ID text for high-level soldiers (yellow)
        if (soldier.level >= UI_CONFIG.HIGH_LEVEL_THRESHOLD) {
          const targetIdText = `#${soldier.id} -> #${soldier.target.id}`;
          // Use scale slightly larger than nebula text (64x32) for visibility
          const debugScale = { x: 80, y: 40 };
          const targetIdSprite = this.createTextSprite(targetIdText, '#ff0', debugScale);  // Yellow
          targetIdSprite.position.set(
            soldier.x - this.width / 2 + 15,  // Offset to the right
            -(soldier.y - this.height / 2),
            20  // High z to appear on top
          );
          this.scene.add(targetIdSprite);
          this.debugSprites.push(targetIdSprite);

          // 4. Show off-screen prediction count if > 0 (purple)
          if (soldier.offScreenPredictionCount > 0) {
            const osText = `OS:${soldier.offScreenPredictionCount}`;
            const osSprite = this.createTextSprite(osText, '#f0f', debugScale);  // Purple, same scale
            osSprite.position.set(
              soldier.x - this.width / 2 + 15,  // Offset to the right
              -(soldier.y - this.height / 2) - 10,  // Below target ID text
              20
            );
            this.scene.add(osSprite);
            this.debugSprites.push(osSprite);
          }
        }
      }
    });

    // Draw base perception and weapon radii
    bases.forEach(base => {
      if (!base.alive) return;

      // Draw perception radius circle for bases
      if (DEBUG_VISUALIZATION_CONFIG.showPerceptionRadius && base.perceptionRange) {
        // Get per-entity animation progress with random delay and easing
        const entityProgress = this.updateEntityAnimation(base, this.debugAnimationProgress);

        const perceptionGeometry = new THREE.RingGeometry(
          base.perceptionRange - 2,  // Inner radius (thicker ring for bases)
          base.perceptionRange,      // Outer radius
          64                          // Segments
        );
        const perceptionMaterial = new THREE.MeshBasicMaterial({
          color: 0x00ffff,  // Cyan
          transparent: true,
          opacity: 0.3 * entityProgress,  // Entity-specific animated opacity
          side: THREE.DoubleSide
        });
        const perceptionCircle = new THREE.Mesh(perceptionGeometry, perceptionMaterial);
        perceptionCircle.position.set(
          base.x - this.width / 2,
          -(base.y - this.height / 2),
          5
        );
        perceptionCircle.scale.setScalar(entityProgress);  // Entity-specific animated scale
        this.scene.add(perceptionCircle);
        this.debugLines.push(perceptionCircle);
      }

      // Draw weapon radius circle for bases
      if (DEBUG_VISUALIZATION_CONFIG.showWeaponRadius && base.weaponRange) {
        // Get per-entity animation progress with random delay and easing
        const entityProgress = this.updateEntityAnimation(base, this.debugAnimationProgress);

        const weaponGeometry = new THREE.RingGeometry(
          base.weaponRange - 2,  // Inner radius (thicker ring)
          base.weaponRange,      // Outer radius
          64                      // Segments
        );
        const weaponMaterial = new THREE.MeshBasicMaterial({
          color: 0xff8800,  // Orange
          transparent: true,
          opacity: 0.3 * entityProgress,  // Entity-specific animated opacity
          side: THREE.DoubleSide
        });
        const weaponCircle = new THREE.Mesh(weaponGeometry, weaponMaterial);
        weaponCircle.position.set(
          base.x - this.width / 2,
          -(base.y - this.height / 2),
          5
        );
        weaponCircle.scale.setScalar(entityProgress);  // Entity-specific animated scale
        this.scene.add(weaponCircle);
        this.debugLines.push(weaponCircle);
      }
    });

    // Collect base stats
    bases.forEach(base => {
      stats.bases.push({
        name: base.team.name,
        health: Math.round(base.health),
        maxHealth: base.maxHealth,
        metal: base.resources.metal
      });
    });

    return stats;
  }

  // Override render() to add WebGL rendering after debug info
  render(soldiers, projectiles, corpses = [], bases = [], resources = []) {
    this.clearScene();
    this.renderEntities(soldiers, projectiles, corpses, bases, resources);
    const stats = this.renderDebugInfo(soldiers, projectiles, bases);

    // Render the scene to canvas AFTER debug visuals are added
    this.renderer.render(this.scene, this.camera);

    return stats;
  }

  resize(width, height) {
    super.resize(width, height);
    
    // Update camera
    const aspect = width / height;
    const frustumSize = height;
    this.camera.left = -frustumSize * aspect / 2;
    this.camera.right = frustumSize * aspect / 2;
    this.camera.top = frustumSize / 2;
    this.camera.bottom = -frustumSize / 2;
    this.camera.updateProjectionMatrix();
    
    // Update renderer
    this.renderer.setSize(width, height);
  }
  
  getRendererType() {
    return 'threejs';
  }
}